import React from 'react';
import { Box, Paper, Typography, TextField, IconButton, Badge, useMediaQuery, Popover } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import { useTheme } from '@mui/material/styles';
import { useChat } from './ChatContext';

function ChatFloatingBox() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { state, sendMessage, markRead, emitTyping, closeChat } = useChat();

  // pick the last opened chat
  const activeId = state.openOrder.length ? state.openOrder[state.openOrder.length - 1] : null;
  const chat = activeId ? state.chats[activeId] : null;
  const friend = activeId ? state.friends.find(f => f.id === activeId) : null;

  const [input, setInput] = React.useState('');
  const [emojiAnchor, setEmojiAnchor] = React.useState(null);
  const openEmoji = (e) => setEmojiAnchor(e.currentTarget);
  const closeEmoji = () => setEmojiAnchor(null);
  const handlePickEmoji = (emo) => {
    setInput((prev) => (prev || '') + emo);
    if (activeId) emitTyping(activeId, true);
  };
  const EMOJIS = [
    // Smileys & Emotion
    '😀','😁','😂','🤣','😃','😄','😅','😆','😉','😊','😋','😎','😍','😘','😗','😙','😚','🙂','🤗','🤩','🤔','🤨','😐','😑','😶','🙄','😏','😣','😥','😮','🤐','😯','😪','😫','🥱','😴','😌','😛','😜','🤪','😝','🤤','😒','😓','😔','😕','🙃','🫠','🫡','🫢','🫣','🫥','😲','☹️','🙁','😖','😞','😟','😤','😢','😭','😦','😧','😨','😩','🤯','😬','😰','😱','🥵','🥶','😳','🤪','😵','🥴','😠','😡','🤬','😷','🤒','🤕','🤢','🤮','🤧','🥵','🥶','🥳','😇','🥹','🫶','❤️','🩷','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','💯','✨','🎉','🔥',
    // People & Body
    '👍','👎','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','👇','☝️','✋','🤚','🖐️','🖖','👋','🤙','💪','🙏','👏','🙌','👐','🤲','✍️','💅','🤳','💃','🕺','🧎','🧍','🧏','🙇','🤦','🤷','👨','👩','🧑','👧','👦','👶','🧓','👴','👵','🧔','👱','👩‍🦰','👨‍🦰','👩‍🦱','👨‍🦱','👩‍🦳','👨‍🦳','👩‍🦲','👨‍🦲','🤝',
    // Animals & Nature
    '🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐨','🐯','🦁','🐮','🐷','🐸','🐵','🐔','🐧','🐦','🐤','🦆','🦅','🦉','🦇','🐺','🐗','🐴','🦄','🐝','🪲','🦋','🐞','🐢','🐍','🦎','🦂','🦀','🐙','🦑','🐠','🐟','🐬','🐳','🦈','🐊','🐆','🐅','🐘','🦏','🦍','🦧','🦣','🦒','🦘','🦬','🐇','🐿️','🦫','🦦','🦨','🦡','🐾','🌸','🌼','🌻','🌹','🌷','🌺','🌵','🌴','🌲','🌳','🌿','🍀','🍁','🍂','🍃','☘️',
    // Food & Drink
    '🍇','🍉','🍊','🍋','🍌','🍍','🥭','🍎','🍏','🍐','🍑','🍒','🍓','🫐','🥝','🍅','🥥','🥑','🍆','🥔','🥕','🌽','🥒','🥬','🧄','🧅','🍄','🥜','🌰','🍞','🥐','🥖','🥨','🥯','🥞','🧇','🧀','🍖','🍗','🥩','🥓','🍔','🍟','🍕','🌭','🥪','🌮','🌯','🥙','🥗','🍝','🍜','🍲','🍛','🍣','🍱','🥟','🍤','🍚','🍙','🍘','🍥','🥠','🍢','🍡','🍧','🍨','🍦','🥧','🧁','🍰','🎂','🍮','🍭','🍬','🍫','🍿','🧋','☕','🍵','🧃','🥤','🍼','🍺','🍻','🍷','🥂','🍸','🍹','🍶',
    // Activities
    '⚽','🏀','🏈','⚾','🎾','🏐','🏉','🎱','🏓','🏸','🥅','🏒','🏑','🥍','🏏','🎿','⛷️','🏂','🏋️','🤼','🤸','⛹️','🤺','🤾','🏌️','⛳','🏇','🧘','🎯','🎳','🎣','🎽','🎮','🕹️','🎲','♟️','🧩',
    // Travel & Places
    '🚗','🚕','🚙','🚌','🚎','🏎️','🚓','🚑','🚒','🚐','🚚','🚛','🚜','✈️','🛩️','🚀','🛸','🚁','🚂','🚆','🚇','🚊','🚉','🚝','🚠','🚡','🚟','🛳️','⛴️','🛥️','🚤','⛵','🗿','🗽','🗼','🗻','🏰','🏯','🏟️','🎡','🎢','🎠','🌋','🏖️','🏝️','🏜️','🏔️','⛺','🏕️',
    // Objects & Symbols
    '⌚','📱','💻','⌨️','🖥️','🖨️','🖱️','💽','💾','💿','📷','🎥','📺','📻','🕰️','⏰','⏱️','⏲️','⏳','🔋','🔌','💡','🔦','🕯️','🧯','🧲','🧪','🧫','🧬','🔬','🔭','📡','💸','💵','💶','💷','💴','💳','🧾','💼','📦','📫','📮','✉️','📧','📨','📩','📤','📥','📅','📆','🗓️','📈','📉','📊','📌','📍','📎','🖇️','📐','📏','✂️','🗄️','🗑️','🔒','🔓','🔑','🗝️','⚙️','🧰','🧲','🪛','🔨','⛏️','⚒️','🛠️','🧱','🧪','🧴','🧹','🧺','🧻','🧼','🧽','🧯','🚿','🛁','🛀','🚽','🚪','🪑','🛏️','🛋️','🖼️','🪞','🪟','🧸','🎁','🎀','🎈','🔔','🔕','🎵','🎶','🎤','🎧','🎷','🎸','🎹','🎺','🎻','🥁','🪇','🪈','🪕','📣','📯','🔊','🔉','🔈','🔇','🏧','🚮','🚰','♿','🚭','🚾','⚠️','⛔','🚸','🚫','✅','❎','❌','⭕','➕','➖','➗','➰','➿','✳️','✴️','❇️','©️','®️','™️','♻️','🔁','🔂','🔀','🔁','🔂','🔄','🔃','🔼','🔽','⏫','⏬','▶️','⏸️','⏹️','⏺️','⏭️','⏮️','⏩','⏪','🔍','🔎','🆗','🆕','🆓','🆒','🆙','🆚','🈯','🈚','🈲','🈳','🈵','🈴','🈺','🈶','🈷️','🈸','㊗️','㊙️'
  ];
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    // mark as read on mount/change
    if (activeId) markRead(activeId);
  }, [activeId, markRead]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // scroll to bottom when messages change
    el.scrollTop = el.scrollHeight;
  }, [chat?.messages?.length]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || !activeId) return;
    sendMessage(activeId, text);
    setInput('');
  };

  // If no active chat, render nothing (we still may show a badge elsewhere)
  if (!activeId || !chat?.open || chat.minimized) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: { xs: 'stretch', md: 'flex-end' },
        pointerEvents: 'none',
        zIndex: (t) => t.zIndex.modal + 2,
        px: { xs: 0, md: 2 },
        pb: 'max(env(safe-area-inset-bottom), 8px)',
      }}
    >
      <Box
        aria-label="Chat box"
        role="dialog"
        sx={{
          pointerEvents: 'auto',
          width: { xs: '100%', md: 380 },
          maxWidth: { md: 400 },
          height: { xs: '48vh', md: 360 },
          mb: { xs: 0, md: 0 },
          borderRadius: { xs: 0, md: 2 },
          boxShadow: 6,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Paper elevation={0} square sx={{ p: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: friend?.online ? 'success.main' : 'text.disabled', mr: 1 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }} noWrap>
            {friend?.name || 'Chat'}
          </Typography>
          {!!(state.chats[activeId]?.unread) && (
            <Badge color="primary" badgeContent={state.chats[activeId]?.unread} />
          )}
          <IconButton aria-label="Close chat" onClick={() => activeId && closeChat(activeId)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>

        {/* Messages */}
        <Paper elevation={0} square sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {chat?.messages?.map((m, idx) => (
              <Typography
                key={m.id || idx}
                sx={{
                  alignSelf: m.from === 'me' ? 'flex-end' : 'flex-start',
                  bgcolor: m.from === 'me' ? 'primary.main' : 'grey.300',
                  color: m.from === 'me' ? 'primary.contrastText' : 'text.primary',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  maxWidth: '75%',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.25,
                }}
              >
                {m.text}
              </Typography>
            ))}
            {chat?.typing && (
              <Typography variant="caption" color="text.secondary">{friend?.name || 'Friend'} is typing…</Typography>
            )}
          </Box>

          {/* Input */}
          <Paper elevation={3} square sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={openEmoji} aria-label="Emoji picker">
              <InsertEmoticonIcon />
            </IconButton>
            <TextField
              value={input}
              onChange={(e) => { setInput(e.target.value); if (activeId) emitTyping(activeId, true); }}
              placeholder="Type a message..."
              variant="outlined"
              size="small"
              fullWidth
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            />
            <IconButton color="primary" onClick={handleSend} aria-label="Send">
              <SendIcon />
            </IconButton>
            <Popover
              open={Boolean(emojiAnchor)}
              anchorEl={emojiAnchor}
              onClose={closeEmoji}
              sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transitionDuration={0}
              slotProps={{
                paper: {
                  sx: {
                    bgcolor: '#ffffff', // solid bright background
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 10,
                    opacity: 1,
                    filter: 'none',
                    backdropFilter: 'none',
                    WebkitBackdropFilter: 'none',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                  },
                },
              }}
            >
              <Box sx={{ p: 1, width: { xs: 280, sm: 340 }, maxHeight: 320, overflowY: 'auto', display: 'grid', gridTemplateColumns: { xs: 'repeat(8, 1fr)', sm: 'repeat(10, 1fr)' }, gap: 0.5 }}>
                {EMOJIS.map((emo) => (
                  <IconButton
                    key={emo}
                    size="small"
                    onClick={() => handlePickEmoji(emo)}
                    aria-label={`Insert ${emo}`}
                    disableRipple
                    disableFocusRipple
                    sx={{ '&:hover': { backgroundColor: 'transparent' }, WebkitTapHighlightColor: 'transparent', color: 'inherit' }}
                  >
                    <Box component="span" sx={{ fontSize: 22, lineHeight: 1, filter: 'contrast(1.08) saturate(1.08)' }}>{emo}</Box>
                  </IconButton>
                ))}
              </Box>
            </Popover>
          </Paper>
        </Paper>
      </Box>
    </Box>
  );
}

function ChatDock() {
  const { state, openChat } = useChat();

  React.useEffect(() => {
    const handler = (e) => {
      const f = e?.detail?.friend;
      if (f && f.id) {
        openChat(f.id, f);
      }
    };
    window.addEventListener('chat:openFriend', handler);
    return () => window.removeEventListener('chat:openFriend', handler);
  }, [openChat]);

  const openedFriends = state.openOrder.map(id => state.friends.find(f => f.id === id)).filter(Boolean);
  const totalUnread = React.useMemo(() => Object.values(state.chats || {}).reduce((sum, c) => sum + (c?.unread || 0), 0), [state.chats]);

  return (
    <>
      {/* Fixed-bottom chat */}
      <ChatFloatingBox />
    </>
  );
}

import { useLocation } from 'react-router-dom';

export default function ChatSystem() {
  const location = useLocation();
  // Feature flag: enable floating chat only if explicitly allowed
  // Enable floating chat by default; allow disabling explicitly by setting REACT_APP_ENABLE_FLOATING_CHAT=false
  const enabled = String(process.env.REACT_APP_ENABLE_FLOATING_CHAT || 'true').toLowerCase() !== 'false';

  // Hide entirely if not enabled
  if (!enabled) return null;

  // Hide floating chat on the dedicated /chat page
  if (location && typeof location.pathname === 'string' && location.pathname.startsWith('/chat')) {
    return null;
  }
  return (
    <ChatDock />
  );
}
